import { Routes } from '@angular/router';
import { redirectLoggedInTo, redirectUnauthorizedTo } from './guards/auth.guard';
import { requireOrg } from './guards/permission.guard';
import { crewdleSuperAdmin, partnerOrCrewdle } from './guards/partner-access.guard';
import { CONTEXT_ORG_PARAM, legacyOrgRedirect, orgFromUrlGuard } from './guards/org-url.guard';

/**
 * Pages that act in an organization (the partner org, or Crewdle for staff).
 * They live under `/o/:contextOrgId/...` — the org of a tab is carried by its
 * URL, not by the user — and their legacy org-less paths redirect into the
 * resolved org.
 */
const ORG_PAGES: Routes = [
  // Landing — KPIs + usage-by-day, scoped to the caller's clients.
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  // Per-client usage drill-down (backend-scoped to the caller's clients).
  {
    path: 'client-usage/:orgId',
    loadComponent: () =>
      import('./pages/client-usage/client-usage.component').then((m) => m.ClientUsageComponent),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  // Commercial: partner pricing (markup) and earnings/payouts.
  {
    path: 'pricing',
    loadComponent: () =>
      import('./pages/pricing/pricing.component').then((m) => m.PricingComponent),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  {
    path: 'earnings',
    loadComponent: () =>
      import('./pages/earnings/earnings.component').then((m) => m.EarningsComponent),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  {
    path: 'notifications',
    loadComponent: () =>
      import('./pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  // Client/partner management — visible to Crewdle staff and resellers.
  // The backend scopes the org list to the caller's clients for resellers.
  {
    path: 'all-organizations',
    loadComponent: () =>
      import('./pages/all-organizations/all-organizations.component').then(
        (m) => m.AllOrganizationsComponent,
      ),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  // In-app partner application — landing page for members of orgs without
  // partner access (the partnerOrCrewdle guard redirects here).
  {
    path: 'become-partner',
    loadComponent: () =>
      import('./pages/become-partner/become-partner.component').then(
        (m) => m.BecomePartnerComponent,
      ),
    canActivate: [requireOrg()],
  },
  // Custom-domain activation gate — Crewdle super-admins only.
  {
    path: 'custom-domains',
    loadComponent: () =>
      import('./pages/custom-domains/custom-domains.component').then(
        (m) => m.CustomDomainsComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  // Agent Arena challenge curation — Crewdle super-admins only (the Arena
  // app has no public posting flow; every challenge is managed here).
  {
    path: 'arena-challenges',
    loadComponent: () =>
      import('./pages/arena-challenges/arena-challenges.component').then(
        (m) => m.ArenaChallengesComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  // Partner application review — Crewdle super-admins only.
  {
    path: 'partner-applications',
    loadComponent: () =>
      import('./pages/partner-applications/partner-applications.component').then(
        (m) => m.PartnerApplicationsComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  // Global platform-config pages — Crewdle super-admins only.
  {
    path: 'workflows-overview',
    loadComponent: () =>
      import('./pages/workflows-overview/workflows-overview.component').then(
        (m) => m.WorkflowsOverviewComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'models',
    loadComponent: () =>
      import('./pages/models/models.component').then((m) => m.ModelsComponent),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'catalog-config',
    loadComponent: () =>
      import('./pages/catalog-config/catalog-config.component').then(
        (m) => m.CatalogConfigComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'products-config',
    loadComponent: () =>
      import('./pages/products-config/products-config.component').then(
        (m) => m.ProductsConfigComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'products-config/chat',
    loadComponent: () =>
      import('./pages/products-config/product-chat-config.component').then(
        (m) => m.ProductChatConfigComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'products-config/support-agent',
    loadComponent: () =>
      import('./pages/products-config/product-support-agent-config.component').then(
        (m) => m.ProductSupportAgentConfigComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'products-config/:productId',
    loadComponent: () =>
      import('./pages/products-config/product-placeholder-config.component').then(
        (m) => m.ProductPlaceholderConfigComponent,
      ),
    canActivate: [requireOrg(), crewdleSuperAdmin],
  },
  {
    path: 'catalog-overrides',
    loadComponent: () =>
      import('./pages/catalog-overrides/catalog-overrides.component').then(
        (m) => m.CatalogOverridesComponent,
      ),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
  {
    path: 'white-label',
    loadComponent: () =>
      import('./pages/white-label/white-label.component').then((m) => m.WhiteLabelComponent),
    canActivate: [requireOrg(), partnerOrCrewdle],
  },
];

export const routes: Routes = [
  {
    // Login is centralized in the external auth portal; the guard hands off to
    // it before this placeholder ever renders. Path kept because other code
    // navigates to /login.
    path: 'login',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectLoggedInTo('/dashboard')],
  },
  {
    // Signup is centralized in the external auth portal; the guard hands off to
    // it before this placeholder ever renders. Path kept because other code
    // navigates to /signup.
    path: 'signup',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectLoggedInTo('/dashboard')],
  },
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    loadComponent: () =>
      import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    path: '__/auth',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth-redirect',
    loadComponent: () =>
      import('./pages/auth-redirect/auth-redirect.component').then(
        (m) => m.AuthRedirectComponent,
      ),
  },
  {
    // Public partner referral link — stores the partner code and redirects a
    // prospect to signup; no auth.
    path: 'r/:code',
    loadComponent: () =>
      import('./pages/referral-redirect/referral-redirect.component').then(
        (m) => m.ReferralRedirectComponent,
      ),
  },
  {
    // Public partner application form — no auth (prospective resellers apply
    // before they have an account).
    path: 'apply',
    loadComponent: () =>
      import('./pages/partner-apply/partner-apply.component').then((m) => m.PartnerApplyComponent),
  },
  {
    // A URL org (`/o/:contextOrgId`) the signed-in user doesn't belong to.
    // Outside the layout: the shell would show the user's other (active) org.
    path: 'no-access',
    loadComponent: () =>
      import('./pages/no-access/no-access.component').then((m) => m.NoAccessComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
    children: [
      {
        path: `o/:${CONTEXT_ORG_PARAM}`,
        canActivate: [orgFromUrlGuard],
        children: [
          ...ORG_PAGES,
          // User-level pages have no org: `/o/{id}/account` → `/account`.
          { path: 'account', redirectTo: '/account' },
          { path: 'settings', redirectTo: '/account' },
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          { path: '**', redirectTo: 'dashboard' },
        ],
      },
      // Legacy org-less URLs → the same page under /o/{resolved org}.
      ...ORG_PAGES.map((page) => ({ path: page.path, canActivate: [legacyOrgRedirect], children: [] })),
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'settings',
        redirectTo: 'account',
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
];
